/**
 * statement.utils.ts
 *
 * Pure utility functions for CSV bank statement parsing and entry matching.
 * Extracted from StatementImport.tsx so they can be unit-tested independently.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type StatementRow = {
    date: Date | null;
    description: string;
    amount: number;
    rawDate: string;
    index: number;
};

export type DraftEntry = {
    title: string;
    amount: string;
    type: 'DEBIT' | 'CREDIT';
    category: string;
    tag: string;
    date: string; // YYYY-MM-DD or ''
    onCredit?: boolean;
};

export type MatchResult = {
    statement: StatementRow;
    matchedEntry: any | null;
    score: number;
    confirmed: boolean;
    skipped: boolean;
    createEntry: boolean;
    draftEntry: DraftEntry | null;
};

// ─── Parsing helpers ──────────────────────────────────────────────────────────

/**
 * Parse a raw amount string from a bank statement into a number.
 * Strips currency symbols, commas, whitespace, and handles em-dash negation.
 *
 * @example
 * parseAmount('$1,234.56') // 1234.56
 * parseAmount('−100')      // -100
 * parseAmount('')          // 0
 */
export const parseAmount = (val: string): number => {
    if (!val || val.trim() === '') return 0;
    return parseFloat(val.replace(/[$,\s]/g, '').replace('−', '-')) || 0;
};

/**
 * Parse a raw date string into a Date object.
 * Supports ISO format (YYYY-MM-DD) and MM/DD/YYYY format.
 * Returns null for empty or unparseable strings.
 *
 * @example
 * parseDate('2024-01-15')  // Date object for Jan 15 2024
 * parseDate('01/15/2024')  // Date object for Jan 15 2024
 * parseDate('')            // null
 */
export const parseDate = (val: string): Date | null => {
    if (!val) return null;
    const iso = new Date(val);
    if (!isNaN(iso.getTime())) return iso;
    const parts = val.split('/');
    if (parts.length === 3) {
        const d = new Date(`${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`);
        if (!isNaN(d.getTime())) return d;
    }
    return null;
};

/**
 * Returns the absolute difference in days between two dates.
 * Returns 999 if either argument is null, acting as a sentinel for "no date".
 */
export const dateDiffDays = (a: Date | null, b: Date | null): number => {
    if (!a || !b) return 999;
    return Math.abs((a.getTime() - b.getTime()) / 86400000);
};

/**
 * Computes a Jaccard-style word overlap score between two description strings.
 * Words shorter than 3 characters are ignored.
 * Returns a value in [0, 1]: 1.0 = identical word sets, 0 = disjoint.
 */
export const wordOverlap = (a: string, b: string): number => {
    const wordsA = new Set(a.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    const wordsB = new Set(b.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    if (!wordsA.size || !wordsB.size) return 0;
    let matches = 0;
    wordsA.forEach(w => { if (wordsB.has(w)) matches++; });
    return matches / Math.max(wordsA.size, wordsB.size);
};

// ─── Matching logic ───────────────────────────────────────────────────────────

/**
 * Score how well a statement row matches a budget entry.
 * Components (max total = 1.0):
 *   - Amount closeness:  0.5 (exact) / 0.3 (within 5%) / 0.1 (within 15%)
 *   - Type match:        0.1
 *   - Date proximity:    0.25 (≤1 day) / 0.15 (≤3 days) / 0.05 (≤7 days)
 *   - Description words: up to 0.15
 */
export const scoreMatch = (stmt: StatementRow, entry: any): number => {
    let score = 0;
    const entryAmount = Number(entry.amount);
    const stmtAbs = Math.abs(stmt.amount);

    if (Math.abs(stmtAbs - entryAmount) < 0.02) score += 0.5;
    else if (Math.abs(stmtAbs - entryAmount) / Math.max(entryAmount, 1) < 0.05) score += 0.3;
    else if (Math.abs(stmtAbs - entryAmount) / Math.max(entryAmount, 1) < 0.15) score += 0.1;

    const stmtType = stmt.amount > 0 ? 'CREDIT' : 'DEBIT';
    if (stmtType === entry.type) score += 0.1;

    if (entry.date) {
        const diff = dateDiffDays(stmt.date, new Date(entry.date));
        if (diff <= 1) score += 0.25;
        else if (diff <= 3) score += 0.15;
        else if (diff <= 7) score += 0.05;
    }

    score += wordOverlap(stmt.description, entry.title || '') * 0.15;
    return Math.min(score, 1);
};

/**
 * Greedily match each statement row to the best available entry.
 *
 * Rules:
 * - Entries flagged as complete, ignored, or child items are excluded.
 * - Each entry may be used at most once (first-wins greedy).
 * - A match is accepted only when score >= 0.4.
 * - A match is auto-confirmed when score >= 0.7.
 *
 * @param statements - Parsed rows from the uploaded bank statement.
 * @param entries    - Budget entries from the application store.
 */
export const buildMatches = (statements: StatementRow[], entries: any[]): MatchResult[] => {
    const available = entries.filter(e => !e.complete && !e.ignore && !e.parent);
    const used = new Set<string>();
    return statements.map(stmt => {
        let best: any = null;
        let bestScore = 0;
        for (const entry of available) {
            if (used.has(entry.id)) continue;
            const s = scoreMatch(stmt, entry);
            if (s > bestScore) { bestScore = s; best = entry; }
        }
        if (best && bestScore >= 0.4) used.add(best.id);
        return {
            statement: stmt,
            matchedEntry: bestScore >= 0.4 ? best : null,
            score: bestScore,
            confirmed: bestScore >= 0.7,
            skipped: false,
            createEntry: false,
            draftEntry: null,
        };
    });
};

/**
 * Converts a Date (or null) to a YYYY-MM-DD string.
 * Returns an empty string for null input.
 */
export const toDateStr = (d: Date | null): string =>
    d ? d.toISOString().slice(0, 10) : '';

/**
 * Build a backend-ready row from a MatchResult for the /statements/confirm endpoint.
 * Category and tag are populated from the draft first, then from the matched entry.
 */
export const buildBackendRow = (m: MatchResult): {
    date: string;
    title: string;
    amount: number;
    type: 'CREDIT' | 'DEBIT';
    category?: string;
    tag?: string;
    onCredit?: boolean;
} => {
    const draft = m.draftEntry;
    const category = draft ? (draft.category || undefined) : m.matchedEntry?.category;
    const tag = draft ? (draft.tag || undefined) : m.matchedEntry?.tag;
    const onCredit = draft?.onCredit;
    return {
        date: draft?.date || (m.statement.date ? m.statement.date.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)),
        title: draft?.title || m.statement.description || 'Imported entry',
        amount: draft?.amount ? Number(draft.amount) : Math.abs(m.statement.amount),
        type: (draft?.type || (m.statement.amount > 0 ? 'CREDIT' : 'DEBIT')) as 'CREDIT' | 'DEBIT',
        ...(category ? { category } : {}),
        ...(tag ? { tag } : {}),
        ...(onCredit != null ? { onCredit } : {}),
    };
};
